import type { Reading } from '@casa/driver-sdk';

/**
 * The data runtime (plan step 1.4, proposal §6.8): one schedule per data source, the latest
 * readings and a status per source for the tiles. It knows nothing of how drivers run; the app
 * connects each connection's driver through a `DriverLink`.
 *
 * - Each source is refreshed on its own interval. Sources of one connection that are due together
 *   go to the driver in one call, and never while that connection's previous call runs.
 * - A failure backs off exponentially, with jitter, up to `maxBackoffS`.
 * - Nothing is refreshed while the app is in the background, nor sources that no shown screen uses;
 *   when either comes back, the sources are refreshed at once.
 * - Status: `waiting` (nothing yet), `ok`, `stale` (no refresh for `staleAfterIntervals`
 *   intervals) or `failing` (the last attempt failed; `failure` says why).
 * - A command goes to the source's driver. With an optimistic value, the source shows it at once
 *   and is held for a lease: no refreshes, and readings the driver sends meanwhile are ignored, so
 *   that a late answer from before the command does not flip the tile back. A failed command ends
 *   the lease, puts back the readings from before it, marks the source failing and refreshes it.
 */

declare const setInterval: (callback: () => void, ms: number) => unknown;
declare const clearInterval: (timer: unknown) => void;
declare const setTimeout: (callback: () => void, ms: number) => unknown;
declare const clearTimeout: (timer: unknown) => void;

export interface RuntimeSource {
    /** Unique in the runtime; what tiles subscribe to. */
    key: string;
    connection: string;
    /** The id the connection's driver knows the source by. */
    sourceId: string;
    intervalS: number;
}

/** What a driver reports back about its sources. */
export interface DriverEvents {
    readings(sourceId: string, readings: Reading[] | null) : void;
    failed(sourceId: string, reason: string) : void;
}

export interface DriverLink {
    /** Asks the driver to refresh these sources; resolves when the driver is done with them. */
    refresh(sourceIds: readonly string[]) : Promise<void>;
    /**
     * Sends a command for a source. Resolves `true` when the driver carried it out, `false` when it
     * does not know the command; rejects when it failed. Absent: the driver takes no commands.
     */
    command?(sourceId: string, name: string, arg?: unknown) : Promise<boolean>;
    dispose() : void;
}

/** The lease after a command: the legacy Hubitat service paused a device's refreshes for 5 s. */
export const COMMAND_LEASE_MS = 5_000;

export type SourceStatus = 'waiting' | 'ok' | 'stale' | 'failing';

export interface SourceState {
    readings: Reading[] | null | undefined;
    status: SourceStatus;
    /** Why the last attempt failed, while `status` is `failing`. */
    failure?: string;
}

export interface RuntimeOptions {
    sources: readonly RuntimeSource[];
    connect(connection: string, events: DriverEvents) : DriverLink;
    /** Milliseconds; `Date.now` by default. */
    now?: () => number;
    random?: () => number;
    /** How often due sources are looked for. Default 250 ms; 0: only through `tick()` (tests). */
    tickMs?: number;
    /** Default 3. */
    staleAfterIntervals?: number;
    /** A source is never stale sooner than this. Default 15 s. */
    minStaleMs?: number;
    /** Default 300 s. */
    maxBackoffS?: number;
    /** A driver call that has not finished after this counts as failed for its sources. Default 120 s. */
    refreshTimeoutMs?: number;
}

export interface Runtime {
    state(key: string) : SourceState;
    subscribe(key: string, listener: () => void) : () => void;
    /** The sources that shown screens use; the others are not refreshed. Default: all. */
    setActive(keys: Iterable<string>) : void;
    setForeground(foreground: boolean) : void;
    /** Shows `readings` at once and holds the source for `ms` (after a command); see above. */
    optimistic(key: string, readings: Reading[] | null, ms?: number) : void;
    /**
     * Sends command `name` to the driver of the source `key`. With `optimistic`, the source shows
     * those readings at once, for the lease. Rejects with the reason when the command fails or the
     * driver does not know it; the source is then failing with that reason until it refreshes.
     */
    command(key: string, name: string, arg?: unknown, optimistic?: Reading[] | null) : Promise<void>;
    /** Looks for due sources now; also run on a timer unless `tickMs` is 0. */
    tick() : void;
    dispose() : void;
}

interface Entry {
    source: RuntimeSource;
    readings: Reading[] | null | undefined;
    lastSuccess: number | undefined;
    lastFailure: number | undefined;
    failure: string | undefined;
    failures: number;
    nextDue: number;
    leaseUntil: number;
    /** The readings from before the lease's optimistic value, put back if the command fails. */
    beforeLease: { readings: Reading[] | null | undefined } | undefined;
    /** When it came back into use (foreground, a shown screen); staleness counts from here at the earliest. */
    resumedAt: number;
    active: boolean;
    status: SourceStatus;
    listeners: Set<() => void>;
}

export function createRuntime(options: RuntimeOptions) : Runtime {
    const now = options.now ?? (() => Date.now());
    const random = options.random ?? Math.random;
    const staleAfter = options.staleAfterIntervals ?? 3;
    const minStaleMs = options.minStaleMs ?? 15_000;
    const maxBackoffS = options.maxBackoffS ?? 300;
    const refreshTimeoutMs = options.refreshTimeoutMs ?? 120_000;

    const entries = new Map<string, Entry>();
    const bySourceId = new Map<string, Map<string, Entry>>();
    const links = new Map<string, DriverLink>();
    const busy = new Set<string>();
    let foreground = true;
    let disposed = false;

    const start = now();
    for (const source of options.sources) {
        const entry: Entry = {
            source,
            readings: undefined,
            lastSuccess: undefined,
            lastFailure: undefined,
            failure: undefined,
            failures: 0,
            nextDue: start,
            leaseUntil: 0,
            beforeLease: undefined,
            resumedAt: start,
            active: true,
            status: 'waiting',
            listeners: new Set(),
        };
        entries.set(source.key, entry);
        const ofConnection = bySourceId.get(source.connection) ?? new Map<string, Entry>();
        ofConnection.set(source.sourceId, entry);
        bySourceId.set(source.connection, ofConnection);
    }

    function statusOf(entry: Entry, at: number) : SourceStatus {
        if (entry.failure !== undefined && entry.lastFailure !== undefined && (entry.lastSuccess === undefined || entry.lastFailure >= entry.lastSuccess)) {
            return 'failing';
        }
        if (entry.readings === undefined) {
            return 'waiting';
        }

        const since = Math.max(entry.lastSuccess ?? 0, entry.resumedAt, entry.leaseUntil);
        const limit = Math.max(minStaleMs, entry.source.intervalS * 1000 * staleAfter);

        return at - since > limit ? 'stale' : 'ok';
    }

    function changed(entry: Entry) : void {
        entry.status = statusOf(entry, now());
        for (const listener of [...entry.listeners]) {
            listener();
        }
    }

    function backoffMs(entry: Entry) : number {
        const seconds = Math.min(entry.source.intervalS * 2 ** Math.max(0, entry.failures - 1), Math.max(maxBackoffS, entry.source.intervalS));

        return seconds * 1000 * (0.8 + 0.4 * random());
    }

    for (const connection of bySourceId.keys()) {
        const sources = bySourceId.get(connection)!;
        links.set(
            connection,
            options.connect(connection, {
                readings(sourceId, readings) {
                    const entry = sources.get(sourceId);
                    // Held by a command's lease: the value shown is the one the command set.
                    if (!entry || disposed || now() < entry.leaseUntil) {
                        return;
                    }

                    entry.readings = readings;
                    entry.lastSuccess = now();
                    entry.failure = undefined;
                    entry.failures = 0;
                    changed(entry);
                },
                failed(sourceId, reason) {
                    const entry = sources.get(sourceId);
                    if (!entry || disposed) {
                        return;
                    }

                    entry.failures++;
                    entry.failure = reason;
                    entry.lastFailure = now();
                    entry.nextDue = entry.lastFailure + backoffMs(entry);
                    changed(entry);
                },
            }),
        );
    }

    async function refreshConnection(connection: string, due: Entry[]) : Promise<void> {
        const link = links.get(connection);
        if (!link) {
            return;
        }

        busy.add(connection);
        const started = now();
        for (const entry of due) {
            entry.nextDue = started + entry.source.intervalS * 1000;
        }

        /** Fails the due sources the driver said nothing about in this call. */
        const failUnreported = (reason: string) : void => {
            for (const entry of due) {
                if ((entry.lastSuccess ?? -1) < started && (entry.lastFailure ?? -1) < started) {
                    entry.failures++;
                    entry.failure = reason;
                    entry.lastFailure = now();
                    entry.nextDue = entry.lastFailure + backoffMs(entry);
                    changed(entry);
                }
            }
        };

        let timer: unknown;
        try {
            await Promise.race([
                link.refresh(due.map((entry) => entry.source.sourceId)),
                new Promise<void>((_, reject) => {
                    timer = setTimeout(() => reject(new Error(`no answer from the driver after ${refreshTimeoutMs / 1000} s`)), refreshTimeoutMs);
                }),
            ]);
            failUnreported('the driver reported nothing for this source');
        } catch (error) {
            failUnreported(error instanceof Error ? error.message : String(error));
        } finally {
            clearTimeout(timer);
            busy.delete(connection);
        }
    }

    function tick() : void {
        if (disposed) {
            return;
        }

        const at = now();
        for (const entry of entries.values()) {
            const status = statusOf(entry, at);
            if (status !== entry.status) {
                changed(entry);
            }
        }
        if (!foreground) {
            return;
        }

        const due = new Map<string, Entry[]>();
        for (const entry of entries.values()) {
            const { connection } = entry.source;
            if (!entry.active || busy.has(connection) || at < entry.nextDue || at < entry.leaseUntil) {
                continue;
            }

            const list = due.get(connection) ?? [];
            list.push(entry);
            due.set(connection, list);
        }
        for (const [connection, list] of due) {
            void refreshConnection(connection, list);
        }
    }

    /** Back in use: refresh at once, and count staleness from now. */
    function resume(entry: Entry, at: number) : void {
        entry.resumedAt = at;
        entry.nextDue = Math.min(entry.nextDue, at);
    }

    function showOptimistic(key: string, readings: Reading[] | null, ms = COMMAND_LEASE_MS) : void {
        const entry = entries.get(key);
        if (!entry) {
            return;
        }

        // A second optimistic value within the lease keeps the readings from before the first.
        if (entry.beforeLease === undefined || now() >= entry.leaseUntil) {
            entry.beforeLease = { readings: entry.readings };
        }
        entry.readings = readings;
        entry.leaseUntil = now() + ms;
        entry.nextDue = Math.max(entry.nextDue, entry.leaseUntil);
        changed(entry);
    }

    const timer = options.tickMs === 0 ? undefined : setInterval(tick, options.tickMs ?? 250);

    return {
        state(key) {
            const entry = entries.get(key);
            if (!entry) {
                return { readings: undefined, status: 'waiting' };
            }

            return { readings: entry.readings, status: entry.status, ...(entry.status === 'failing' && entry.failure ? { failure: entry.failure } : {}) };
        },
        subscribe(key, listener) {
            const entry = entries.get(key);
            entry?.listeners.add(listener);

            return () => entry?.listeners.delete(listener);
        },
        setActive(keys) {
            const active = new Set(keys);
            const at = now();
            for (const entry of entries.values()) {
                const isActive = active.has(entry.source.key);
                if (isActive && !entry.active) {
                    resume(entry, at);
                }
                entry.active = isActive;
            }
            tick();
        },
        setForeground(value) {
            if (value === foreground) {
                return;
            }

            foreground = value;
            if (value) {
                const at = now();
                for (const entry of entries.values()) {
                    resume(entry, at);
                }
                tick();
            }
        },
        optimistic: showOptimistic,
        async command(key, name, arg, optimisticReadings) {
            const entry = entries.get(key);
            if (!entry) {
                throw new Error(`no source ${key}`);
            }

            const link = links.get(entry.source.connection);
            if (optimisticReadings !== undefined) {
                showOptimistic(key, optimisticReadings);
            }

            try {
                if (!link?.command) {
                    throw new Error('this driver takes no commands');
                }
                if (!(await link.command(entry.source.sourceId, name, arg))) {
                    throw new Error(`the driver does not know the command "${name}"`);
                }
            } catch (error) {
                const reason = `command ${name} failed: ${error instanceof Error ? error.message : String(error)}`;
                entry.leaseUntil = 0;
                // The value that failed is not shown on: the readings from before it are, until the refresh.
                if (entry.beforeLease !== undefined) {
                    entry.readings = entry.beforeLease.readings;
                    entry.beforeLease = undefined;
                }
                entry.failure = reason;
                entry.lastFailure = now();
                entry.nextDue = entry.lastFailure;
                changed(entry);
                tick();

                throw new Error(reason, { cause: error });
            }
        },
        tick,
        dispose() {
            disposed = true;
            clearInterval(timer);
            for (const link of links.values()) {
                link.dispose();
            }
        },
    };
}
